import { useMemo, useCallback, Dispatch, SetStateAction, useState, ChangeEvent, ReactNode } from 'react'

import { SortDescriptor, orderBy, process } from '@progress/kendo-data-query'
import MD5 from 'crypto-js/md5'
import { v4 as makeUuid } from 'uuid'
import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { isPending } from '@reduxjs/toolkit'
import { patchAccountLightweightSettings, PatchOperation } from 'redux/features/settings/settingsApiThunks'
import { AvailableSettings, BulkEmailExemptPolicy } from 'types/Settings'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import routesConfig from 'lib/routesConfig'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { useSettingTableCellRadios } from '../../atpSettings/useSettingsTableCellRadio'
import {
  BulkEmailExemptTableForm,
  BulkEmailExemptTableFormState,
  useBulkEmailExemptTableForm
} from './useBulkEmailExemptTableForm'

const INITIAL_FORM_STATE: BulkEmailExemptPolicy = {
  id: '',
  name: '',
  comment: '',
  type: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT
}
const initialSort = [{ field: 'name', dir: 'asc' } as SortDescriptor]

export const enum INPUT_NAMES {
  NAME = 'name',
  TYPE = 'type',
  COMMENT = 'comment'
}

export interface BulkEmailExemptProps {
  delegateDirtyFormState: Dispatch<SetStateAction<boolean>>
}

export interface State {
  isLoading: boolean
  newEmailTypeRadios: Map<
    AvailableSettings.BULK_EMAIL_SENDER_EXEMPT | AvailableSettings.BULK_EMAIL_RCPT_EXEMPT,
    ReactNode
  >
  itemToRemove?: BulkEmailExemptPolicy
  sort: SortDescriptor[]
  form: BulkEmailExemptTableForm
  formState: BulkEmailExemptTableFormState
  error: string
  tableData: {
    data: BulkEmailExemptPolicy[]
    total: number
  }
}

export interface EventHandlers {
  onAdd: () => void
  onRemove: (item: BulkEmailExemptPolicy) => void
  onBulkEdit: () => void
  onConfirmRemove: () => void
  onConfirmCancel: () => void
  handleOnInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleSortChange: (e: GridSortChangeEvent) => void
}

export type UseBulkEmailExemptTableLogic = [State, EventHandlers]

export const useBulkEmailExemptTableLogic = ({
  delegateDirtyFormState
}: BulkEmailExemptProps): UseBulkEmailExemptTableLogic => {
  const dispatch = useAppDispatch()
  const isReadonly = useIsReadonly()
  const { accountSettings, updateAccountSettingPending } = useAppSelector(_stores => ({
    accountSettings: _stores.settings.accountSettings,
    updateAccountSettingPending: isPending(_stores.settings.updateAccountSettingsApiStatus)
  }))

  const data = useMemo(() => {
    const senderExemptions = (accountSettings.bulk_email_sender_exempt as BulkEmailExemptPolicy[]) || []
    const rcptExemptions = (accountSettings.bulk_email_rcpt_exempt as BulkEmailExemptPolicy[]) || []
    return [
      ...senderExemptions.map(item => ({ ...item, type: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT })),
      ...rcptExemptions.map(item => ({ ...item, type: AvailableSettings.BULK_EMAIL_RCPT_EXEMPT }))
    ] as BulkEmailExemptPolicy[]
  }, [accountSettings])

  const onAddItem = useCallback(
    (item: BulkEmailExemptPolicy) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.ADD,
          value: {
            name: item.name,
            comment: item.comment,
            id: item.id
          },
          variable: item.type
        })
      )
    },
    [dispatch]
  )

  const onRemoveItem = useCallback(
    (item: BulkEmailExemptPolicy) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.REMOVE,
          value: { id: item.id },
          variable: item.type
        })
      )
    },
    [dispatch]
  )

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_BULK_EMAIL_EXEMPT_BULK_EDIT.goto()
  }, [])

  const form = useBulkEmailExemptTableForm({
    initialState: INITIAL_FORM_STATE,
    delegateIsDirtyForm: delegateDirtyFormState
  })
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort)
  const [itemToRemove, setItemToRemove] = useState<BulkEmailExemptPolicy | undefined>(undefined)

  const onAdd = useCallback(() => {
    const newEntry = {
      ...form.getState(),
      id: MD5(makeUuid()).toString()
    }
    if (form.validate(newEntry, { data })) {
      onAddItem(newEntry)
    }
  }, [form, data, onAddItem])

  const onRemove = useCallback(
    (item: BulkEmailExemptPolicy) => {
      setItemToRemove(item)
    },
    [setItemToRemove]
  )

  const onConfirmRemove = useCallback(() => {
    if (itemToRemove) {
      onRemoveItem(itemToRemove)
    }
    setItemToRemove(undefined)
  }, [itemToRemove, onRemoveItem, setItemToRemove])

  const onConfirmCancel = useCallback(() => {
    setItemToRemove(undefined)
  }, [setItemToRemove])

  const tableData = useMemo(() => {
    const orderedData = orderBy(data, sort)
    const { data: processedData } = process(orderedData, {})

    // Have the first item as an edit item.  This method works with sorting.
    processedData.unshift(INITIAL_FORM_STATE)
    return {
      data: processedData as BulkEmailExemptPolicy[],
      total: processedData.length || 0
    }
  }, [data, sort])

  const handleSortChange = useCallback((e: GridSortChangeEvent) => {
    setSort(e.sort)
  }, [])

  const handleOnInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      // eslint-disable-next-line default-case
      switch (true) {
        case name === INPUT_NAMES.NAME:
          form.fields.name.setValue(value)
          break
        case name === INPUT_NAMES.COMMENT:
          form.fields.comment.setValue(value)
          break
      }
      form.delegateIsDirty()
    },
    [form]
  )

  const onClickNewEmailType = useCallback(
    (type: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT | AvailableSettings.BULK_EMAIL_RCPT_EXEMPT) => {
      form.fields.type.setValue(type)
    },
    [form]
  )

  const newEmailTypeRadios = useSettingTableCellRadios<
    AvailableSettings.BULK_EMAIL_SENDER_EXEMPT | AvailableSettings.BULK_EMAIL_RCPT_EXEMPT
  >(
    [AvailableSettings.BULK_EMAIL_SENDER_EXEMPT, AvailableSettings.BULK_EMAIL_RCPT_EXEMPT],
    onClickNewEmailType,
    form.fields.type.getValue() as any,
    isReadonly
  )

  return useMemo(
    () => [
      {
        isLoading: updateAccountSettingPending,
        newEmailTypeRadios,
        itemToRemove,
        sort,
        form,
        formState: form.getState(),
        error: form.error,
        tableData
      },
      {
        onAdd,
        onRemove,
        onBulkEdit,
        onConfirmRemove,
        onConfirmCancel,
        handleOnInputChange,
        handleSortChange
      }
    ],
    [
      newEmailTypeRadios,
      updateAccountSettingPending,
      itemToRemove,
      sort,
      form,
      tableData,
      onAdd,
      onRemove,
      onBulkEdit,
      onConfirmRemove,
      onConfirmCancel,
      handleOnInputChange,
      handleSortChange
    ]
  )
}
